import { ComponentProps, ReactElement, ReactNode } from 'react';
import {
  ChartDimensions,
  ChartSize,
  ChartTheme,
  BarDatum,
  TooltipVariant,
  CategoryData,
  ChartAxesProps,
  MarginProps,
  LinearData,
  TwoCategoryData,
  Statistics,
  LinearDim,
} from '../../types';
import {
  ScaleTime as d3ScaleTime,
  ScaleLinear as d3ScaleLinear,
  ScaleOrdinal as d3ScaleOrdinal,
  ScalePoint as d3ScalePoint,
} from 'd3';
import { ChartContainerProps } from '../ChartContainer';
import { BarDirection, BarOrientation, BarProps } from '../Bar';
import { SortingMethod } from '../../utils';
import { FormatMessage, Theme, ValueOf } from '@redsift/design-system';
import { LegendProps } from '../Legend';
import { AnchorProps } from '../DataPoint';

/**
 * Component's labels variant.
 */
export const BarChartLegendVariant = {
  none: 'none',
  externalLabel: 'externalLabel',
  externalLabelValue: 'externalLabelValue',
  externalLabelPercent: 'externalLabelPercent',
  custom: 'custom',
} as const;
export type BarChartLegendVariant = ValueOf<typeof BarChartLegendVariant>;

interface LocaleText {
  emptyChartText: string;
}

export type BarChartDimensions = ChartDimensions & {
  fontSize: number;
  minWidth?: number;
  maxWidth?: number;
};

export type XScaleType = 'number' | 'Date' | 'dateString' | undefined;

/**
 * Component props.
 */
export interface BarChartProps extends ChartContainerProps, ChartAxesProps {
  /** @deprecated: Use xAxisTickRotation instead. Whether the x axis labels are rotated or not. */
  areXLabelsRotated?: boolean;
  /** Native HTML props to forward to each bar. */
  barProps?: Omit<ComponentProps<'g'>, 'onClick' | 'orientation' | 'ref' | 'scale' | 'direction'> & {
    minWidth?: number;
    gap?: number;
  };
  /** Bar role. If an onClick is provided, the bars will have the role `button`. For a navigation link, please use `getBarAnchorProps` instead. */
  barRole?: BarProps['role'];
  /** Number of categories to use, the rest being put into a new category called "Others". */
  caping?: number;
  /** Color palette to use. You can choose among the list of available color palette or also choose to use the success/warning/danger theme for which you have to specify which key corresponds to which status.  */
  colorTheme?: ChartTheme;
  /** Dataset to use to generate the chart, should be a list of objects containing a key and a value. */
  data?: CategoryData | LinearData | TwoCategoryData;
  /** Data type override to skip data type inferrence. */
  dataType?: 'TwoCategoryData' | 'LinearData' | 'CategoryData';
  /**
   * Direction of the bars. Only applied when the orientation is vertical. By default bars go "up" but can go "down".
   * They can also go both ways in which case you need to indicates which category go which direction. The concatenation of the two arrays will be used to determine the order of the categories instead of the sortingMethod prop.
   */
  direction?: BarDirection;
  /** Component to use if the chart is empty (replacing the default one). */
  emptyComponent?: ReactNode;
  /** Method used to define the anchor props to use when the bar is a navigation link. */
  getBarAnchorProps?: (datum: BarDatum) => AnchorProps;
  /** Method to determine whether a bar is selected or not. */
  isBarSelected?: (datum: BarDatum) => void;
  /** Method to override the data labels. */
  labelDecorator?: (
    datum: BarDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Method modifying what's displayed within the legend when the legendVariant is "custom". */
  legendDecorator?: (
    datum: BarDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Define whether the labels should be displayed inside or outside the charts and if they should contain raw or percentage values. If set to "custom", use `legendDecorator` to customize it. */
  legendVariant?: BarChartLegendVariant;
  /** Props to forward to the Legend block. Can be used to make the legend selectable. */
  legendProps?: Omit<LegendProps, 'data' | 'ref' | 'variant' | 'width'> & { extraLegendItems?: Statistics };
  /** Labels and texts. */
  localeText?: LocaleText;
  /** Custom margins, used to give more space for axes labels and legend for instance. */
  margins?: MarginProps;
  /** Method to be called on a click on a bar. For a navigation link, please use `getBarAnchorProps` instead. */
  onBarClick?: (datum: BarDatum, data: BarDatum[]) => void;
  /** Orientation of the bar. */
  orientation?: BarOrientation;
  /** Whether a category should be displayed for categories that has been removed by the caping option. Optionaly, this prop can be used to change the label of this category. */
  others?: boolean | string;
  /** BarChart size. */
  size?: ChartSize | BarChartDimensions;
  /** Define how to sort categories. */
  sortingMethod?: SortingMethod;
  /** Method modifying what's displayed within the tooltip when the tooltipVariant is "custom".  */
  tooltipDecorator?: (
    data: BarDatum,
    props?: { index?: number; isSelected?: boolean; color?: string },
    dataset?: ComputedBarProps[],
    groupedData?: ComputedBarProps[]
  ) => string | ReactElement;
  /** Tooltip variant. */
  tooltipVariant?: TooltipVariant | 'grouped';
  /** Theme. */
  theme?: Theme;
  /** X-scale type override to skip x-scale type inferrence. */
  xScaleType?: XScaleType;
  /**
   * In case of a datetime x-axis, if the xAxisTickValues prop is passed with a d3.js method having an offset function (for instance `timeDay`), the following prop will be used as argument.
   * For reference, the line of code using it is: xAxisTickValues.offset(new Date(domain[0]), xAxisStartDateOffset)
   */
  xAxisStartDateOffset?: number;
  /**
   * In case of a datetime x-axis, if the xAxisTickValues prop is passed with a d3.js method having an offset function (for instance `timeDay`), the following prop will be used as argument.
   * For reference, the line of code using it is: xAxisTickValues.offset(new Date(domain[1]), xAxisEndDateOffset)
   */
  xAxisEndDateOffset?: number;
  /** If set, the Y axis will use a logarithmic scale using this base. Set this value to 10 to use a default logarithmic scale. */
  yScaleLogBase?: number;
}

export type StyledBarChartProps = BarChartProps & {};

export interface RenderedLinearBarChartProps extends Omit<BarChartProps, 'data' | 'caping' | 'orientation' | 'others'> {
  data: LinearData | TwoCategoryData;
}
export interface ComputedBarProps {
  category: string;
  key: LinearDim;
  value: number;
  cumulativeValue: number;
}

export interface BarChartState
  extends ChartAxesProps,
    Required<Pick<RenderedLinearBarChartProps, 'margins'>>,
    Pick<
      RenderedLinearBarChartProps,
      | 'barProps'
      | 'barRole'
      | 'colorTheme'
      | 'direction'
      | 'disableAnimations'
      | 'getBarAnchorProps'
      | 'id'
      | 'isBarSelected'
      | 'labelDecorator'
      | 'onBarClick'
      | 'tooltipVariant'
      | 'tooltipDecorator'
    > {
  bars: ComputedBarProps[];
  barWidth: number;
  cache: React.MutableRefObject<LinearData | TwoCategoryData | undefined>;
  categories: string[];
  chartHeight: number;
  chartWidth: number;
  colorScale?: d3ScaleOrdinal<string, string>;
  format: FormatMessage;
  gap: number;
  hasCategory: boolean;
  scaleX: d3ScaleLinear<number, number, never> | d3ScaleTime<number, number, never> | d3ScalePoint<string>;
  scaleY: d3ScaleLinear<number, number>;
  sectionHeight?: number;
  xScaleType?: XScaleType;
  regions?: BarChartSectionProps['regions'];
}

export interface BarChartSectionProps extends Omit<ComponentProps<'g'>, 'onClick' | 'role' | 'color'> {
  /** Starting point in percentage of the chart height. */
  from?: number;
  /** Ending point in percentage of the chart height. */
  to?: number;
  /** Color. */
  color?:
    | 'red'
    | 'green'
    | {
        textColor: string;
        backgroundColor?: string;
      };
  /** Section title */
  title?: string;
  /** Title position relative to section */
  titlePosition?: 'left' | 'center' | 'right';
  scaleX?: d3ScaleLinear<number, number, never> | d3ScaleTime<number, number, never> | d3ScalePoint<string>;
  scaleY?: d3ScaleLinear<number, number, never>;
  direction?: BarDirection;
  /** Regions for highlighting a specific area by changing the background color. Only works with datestrings for X axis. */
  regions?: {
    from: string;
    to: string;
    backgroundColor?: string;
    backgroundOpacity?: number;
    colorTheme?: Record<string, string>;
    tooltipDecorator?: BarChartProps['tooltipDecorator'];
  }[];
}

export interface StyledBarChartSectionProps {
  $theme: Theme;
  $textColor: string;
}

export interface BarChartBarsProps extends Omit<ComponentProps<'g'>, 'onClick' | 'role'> {
  category?: string;
}

export type StyledBarChartBarsProps = Omit<BarChartBarsProps, 'category'> & {};

export interface BarChartOverlayProps extends Omit<ComponentProps<'g'>, 'onClick' | 'role'> {
  from?: number;
  to?: number;
  regions?: BarChartSectionProps['regions'];
}

export type StyledBarChartOverlayProps = Omit<BarChartOverlayProps, 'category'> & {};
